'use client';

import type { ReactElement } from 'react';
import type { ApiError } from '@/app/hooks/useError';
import type { User } from '@/types/user/type';
import type { AnswerType } from '~/model/AnswerType';
import type { ConclusionType } from '~/model/ConclusionType';
import type { ContributionType } from '~/model/ContributionType';
import type { LocaleType } from '~/model/GlobalTypes';
import type { InteractionType } from '~/model/InteractionType';
import type { QuestionReconstructedType } from '~/model/QuestionType';
import { VpButton, VpIcon } from '@vtmn-play/react/headless';
import { useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';
import { CrossSell } from '@/app/[locale]/(form)/components/CrossSell';
import { useFormContext } from '@/app/[locale]/(form)/components/FormProvider';
import { useContributorContext } from '@/app/[locale]/(form)/components/provider/ContributorProvider';
import { fetchItem } from '@/app/actions/server';
import { handleAnswers } from '@/app/actions/server/answers';
import { Loader } from '@/app/components/Loader';
import { MainColumn } from '@/app/components/MainColumn';
import { useApiErrorContext } from '@/app/components/Provider';
import { useSlideAnimationContext } from '@/app/components/Provider/SlideAnimationProvider';
import { Translate } from '@/app/components/Translate/Translate';
import { dataLayerPush } from '@/app/utils/analytics/datalayer';
import { checkContributionCount } from '@/app/utils/contributions/count';
import { getEntityTranslationVersion } from '@/app/utils/entities/translation';
import { getIdFromUri } from '@/app/utils/entities/uri';
import { isGuestUser, isMainUser } from '@/app/utils/entities/user';
import { Link, usePathname } from '@/i18n/routing';
import { CompleteProfileBannerForm } from '~/components/commons/CompleteProfileBanner/CompleteProfileBannerForm';
import { journeyColumnTemplate } from '~/components/Journey/config';
import { RewardsBanner } from '~/components/Rewards/RewardsBanner';
import {
  apiTypes,
  statusContribution,
  transitionsContribution,
} from '~/const/api';
import {
  AnalyticEvents,
  ComponentTranslationStatus,
  ElementType,
  UserRewardsStatus,
} from '~/const/appConst';
import { useUserJourney } from '~/hooks/interaction/form/useUserJourney';
import { RequestStatus, useRequestState } from '~/hooks/request/useRequestState';
import { InteractionTags } from '~/model/InteractionType';
import { answerValidator } from '~/utils/answer/answer';
import { contributionHasToBeSubmitted } from '~/utils/contribution/contribution';
import { getFormattedAnswerValue } from '~/utils/utils';
import { Conclusion } from '../Conclusion/Conclusion';
import { checkIsSelection, checkIsSurvey } from '../utils';
import { FormNavigation } from './FormNavigation/FormNavigation';
import { Question } from './Question/Question';
import { EmailTypeQuestion } from './Question/Types/Email';
import { Section } from './Section/Section';
import {
  formatAnswerUser,
  formatAnswerUserPostData,
  isSectionEmpty,
  shouldAnswerBeDeleted,
} from './utils';

export const InteractionForm = ({
  children,
  interaction,
  langValue,
  languageToDisplay,
  formPreview = false,
  userLanguage,
  getTranslation,
  translated,
  canHaveMultipleContributions,
}: {
  children?: ReactElement;
  interaction: InteractionType;
  langValue: string;
  languageToDisplay: LocaleType;
  formPreview?: boolean;
  userLanguage: LocaleType;
  getTranslation: () => void;
  translated: boolean;
  updateContribution?: (fromForm?: boolean) => Promise<ContributionType[]>;
  setHasToDisplayResults?: (value: boolean) => void;
  canHaveMultipleContributions?: boolean;
  hasAlreadyContributed?: boolean;
}) => {
  const pathname = usePathname();
  const t = useTranslations('FORM');

  const { setError } = useApiErrorContext();
  const { contributions, contributor, answers, setAnswers, getContributions } = useContributorContext();
  const { isSliding, animateSlide } = useSlideAnimationContext();
  const { sections } = useFormContext();
  const { status, startRequest, handleSuccess } = useRequestState();

  const {
    answerUser,
    currentStep,
    currentStepIndex,
    isLastStep,
    isLoading,
    userJourney,
    handleNextPrev,
    setIsLoading,
    setCurrentStepIndex,
    setAnswerUser,
    getConclusionOnConditional,
  } = useUserJourney(
    languageToDisplay,
    formPreview,
  );

  const [isFormEnd, setIsFormEnd] = useState(false);
  const [shouldDisplayRadioSubmitBtn, setShouldDisplayRadioSubmitBtn] = useState(true);
  const [userEmail, setUserEmail] = useState(contributor?.mail || '');

  const { SECTION, CONCLUSION, QUESTION } = ElementType;

  const userContribution = contributions?.[0];
  const isSelection = checkIsSelection(interaction);
  const isSurvey = checkIsSurvey(interaction);

  const hasSubmittedContribution
    = !!userContribution?.id
      && userContribution.status === statusContribution.submitted;

  const isContributionInitialized
    = !!userContribution?.id
      && userContribution.status === statusContribution.initialized;

  const isProcessing = isLoading || isSliding || status === RequestStatus.PENDING;
  const submitButtonLabel = isLastStep ? t('VALIDATE') : t('NEXT_BUTTON');

  const isCurrentStepQuestion = currentStep?.['@type'] === QUESTION;
  const isCurrentStepConclusion = currentStep?.['@type'] === CONCLUSION;
  const isCurrentStepEmail
    = (currentStep as QuestionReconstructedType)?.type === apiTypes.EMAIL;
  const isMandatory = (currentStep as QuestionReconstructedType)?.mandatory;
  const hasCurrentAnswer = !!answerUser[currentStep?.['@id']];
  const hasPreviousAnswer
    = isCurrentStepQuestion
      && answers?.some(answer => currentStep['@id'] === answer.question);

  const hasValidAnswer = answerValidator(
    currentStep as QuestionReconstructedType,
    answerUser[currentStep?.['@id']],
  );

  const emailRegEx = /^[\w.%+-]+@[a-z0-9.-]+\.[a-z]{2,}$/i;
  const isEmailFormatValid = emailRegEx.test(userEmail);
  const isEmailValid = userEmail.length === 0 || isEmailFormatValid;

  const shouldHaveAnswer = !hasValidAnswer && isMandatory;
  const checkIfPreviousStepDisabled = currentStepIndex === 0 || isProcessing;

  const checkIfNextStepDisabled
    = isProcessing || shouldHaveAnswer || (isCurrentStepEmail && !isEmailValid);

  const shouldSubmitOnAnswerUserChange
    = isCurrentStepQuestion
      && !isLastStep
      && !hasPreviousAnswer
      && !(currentStep as QuestionReconstructedType).other_answer
      && ([apiTypes.CONDITIONAL, apiTypes.CHOICES_JAR].includes(
        (currentStep as QuestionReconstructedType).type,
      )
      || (isMandatory && apiTypes.RADIO
      === (currentStep as QuestionReconstructedType).type
      ));

  const shouldDisplaySubmitButton
    = (isCurrentStepQuestion || isCurrentStepEmail)
      && shouldDisplayRadioSubmitBtn
      && !shouldSubmitOnAnswerUserChange;

  const shouldDisplayRewardsBanner
    = interaction.tag === InteractionTags.SURVEY_QUANTITATIVE
      && contributor?.status === UserRewardsStatus.lucky
      && (!canHaveMultipleContributions || (canHaveMultipleContributions && !checkContributionCount({
        count: interaction.current_user_contribution.count_per_status,
        key: 'submitted',
        value: 1,
      })));

  const handleChangeAnswer = (
    value: string,
    questionId: string,
    type: string,
    subQuestionId?: string,
    other_answer?: boolean,
    media?: string,
  ) => {
    setAnswerUser(
      formatAnswerUser({
        type,
        answerUser,
        questionId,
        subQuestionId,
        formatAnswerValue: getFormattedAnswerValue(value, type),
        value,
        other_answer,
        media,
      }),
    );
  };

  const updatePreviousAnswers = (updatedAnswers: AnswerType[]) => {
    const isToDelete = shouldAnswerBeDeleted(
      answerUser[currentStep['@id']],
      currentStep['@id'],
      updatedAnswers,
    );

    setAnswers(
      [...updatedAnswers, ...answers]
        .filter(
          (answer, index, self) =>
            index === self.findIndex(t => t['@id'] === answer['@id']),
        )
        .filter(
          answer => !(answer.question === currentStep['@id'] && isToDelete),
        ),
    );
  };

  const updateAnswers = async (question: QuestionReconstructedType) => {
    const updatedAnswers = await handleAnswers({
      answers: formatAnswerUserPostData(
        answerUser[question['@id']],
        question['@id'],
        userContribution,
        answers,
      ),
    });

    if (updatedAnswers.some(answer => answer['hydra:description'])) {
      throw updatedAnswers.find(answer => answer['hydra:description']);
    }

    updatePreviousAnswers(updatedAnswers);

    return updatedAnswers;
  };

  const sendDataLayerDisplayResult = () => {
    dataLayerPush({
      event: AnalyticEvents.RESULTS_DISPLAYED,
      user_id: `${getIdFromUri(userContribution.created_for['@id'])}`,
      page_project_id: interaction.project.id.toString(),
      page_interaction_id: interaction.id.toString(),
    });
  };

  const handleSubmitContributionAndRefresh = async (): Promise<void> => {
    if (userContribution.status === statusContribution.initialized) {
      await fetchItem<ContributionType>({
        pathname: `contributions/${userContribution.id}`,
        revalidate: 0,
        method: 'PUT',
        body: {
          transition: transitionsContribution.submit,
        },
      });

      dataLayerPush({
        event: AnalyticEvents.FORM_SUBMIT,
        user_id: `${getIdFromUri(userContribution.created_for['@id'])}`,
        page_project_id: interaction.project.id.toString(),
        page_interaction_id: interaction.id.toString(),
      });
    }

    if (isSelection) {
      return setIsFormEnd(true);
    }

    await getContributions();
  };

  const handleSubmitQuestion = async (nextIndex = currentStepIndex + 1) => {
    setIsLoading(true);

    const question = currentStep as QuestionReconstructedType;
    const conclusion = getConclusionOnConditional(question);
    const shouldSubmit
      = !(isGuestUser(contributor) && interaction.result_visible_on_front)
        && contributionHasToBeSubmitted(
          answerUser,
          conclusion,
          currentStepIndex,
          isLastStep,
          nextIndex,
          question,
          userJourney[nextIndex],
        );

    try {
      if (hasCurrentAnswer) {
        await updateAnswers(question);
      }

      if (shouldSubmit) {
        await handleSubmitContributionAndRefresh();
      }
      setIsLoading(false);
      setShouldDisplayRadioSubmitBtn(true);
    } catch (error) {
      setError(error as ApiError);
      setIsLoading(false);
      throw error;
    }
  };

  const isEnTranslationValid = currentStep?.translations?.en_GB?.status === ComponentTranslationStatus.valid;
  const originalLocale = currentStep?.original_locale;

  const currentStepData = getEntityTranslationVersion({
    entity: currentStep,
    entityTranslated: translated,
    isEnTranslationValid,
    originalLocale,
    userLanguage: languageToDisplay,
  });

  const handleSubmit = async () => {
    startRequest();

    if (
      isGuestUser(contributor)
      && isCurrentStepEmail
      && !!userEmail.length
      && isEmailFormatValid
    ) {
      await fetchItem<User>({
        pathname: 'guest_users/me',
        body: { mail: userEmail },
        method: 'PUT',
        revalidate: 0,
      });
    }

    await handleNextPrev(
      currentStepIndex + 1,
      handleSubmitQuestion,
      animateSlide,
    );

    handleSuccess();
  };

  const translateButtonComponent = (
    <Translate
      getTranslation={getTranslation}
      originalLanguage={currentStep?.original_locale}
      userLanguage={userLanguage}
      translated={translated}
      entityUri={currentStep?.['@id']}
      isEnTranslationValid={isEnTranslationValid}
    />
  );

  useEffect(() => {
    if (
      isCurrentStepConclusion
      && !formPreview
      && !isProcessing
      && !isFormEnd
      && isContributionInitialized
    ) {
      handleSubmitContributionAndRefresh();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCurrentStepConclusion, isProcessing, userContribution]);

  useEffect(() => {
    if (shouldSubmitOnAnswerUserChange && hasCurrentAnswer) {
      if (!formPreview) {
        setShouldDisplayRadioSubmitBtn(false);
      }

      handleNextPrev(currentStepIndex + 1, handleSubmitQuestion, animateSlide);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [answerUser]);

  return (
    <>
      <Loader size="medium" loading={isLoading} centered>
        {children}
        {currentStep?.['@type'] === SECTION && !(isSectionEmpty(currentStep) && currentStepIndex === 0) && (
          <MainColumn column={journeyColumnTemplate}>
            <Section
              section={currentStepData}
              langValue={langValue}
              number={sections.indexOf(
                sections.find(section => section['@id'] === currentStep['@id']),
              ) + 1}
              start={() =>
                animateSlide({
                  callback: async () => setCurrentStepIndex(currentStepIndex + 1),
                  back: false,
                })}
              translateButtonComponent={translateButtonComponent}
            />
          </MainColumn>
        )}
        {isCurrentStepQuestion && (
          <Question
            key={`question-${currentStep.id}`}
            answerUser={answerUser}
            currentQuestion={{
              ...currentStep,
              title: currentStepData.title,
              description: currentStepData.description,
              choices: (currentStep as QuestionReconstructedType).choices.map((choice) => {
                const { title } = getEntityTranslationVersion({
                  entity: choice,
                  entityTranslated: translated,
                  isEnTranslationValid,
                  originalLocale,
                  userLanguage: languageToDisplay,
                });

                return {
                  ...choice,
                  title,
                };
              }),
              sub_questions: (currentStep as QuestionReconstructedType).sub_questions.map((subQuestion) => {
                const { title } = getEntityTranslationVersion({
                  entity: subQuestion,
                  entityTranslated: translated,
                  isEnTranslationValid,
                  originalLocale,
                  userLanguage: languageToDisplay,
                });

                return {
                  ...subQuestion,
                  title,
                };
              }),
            } as QuestionReconstructedType}
            disabled={isProcessing}
            langValue={langValue}
            handleChangeAnswer={handleChangeAnswer}
            handleLoading={setIsLoading}
          />
        )}
        {isCurrentStepEmail && (
          <EmailTypeQuestion
            userEmail={userEmail}
            userLanguage={userLanguage}
            setUserEmail={setUserEmail}
            error={!!userEmail.length && !isEmailFormatValid}
            valid={!!userEmail.length && isEmailFormatValid}
          />
        )}
        {isCurrentStepConclusion && (
          <>
            {shouldDisplayRewardsBanner && <RewardsBanner />}
            <Conclusion
              conclusion={{
                ...(currentStep as ConclusionType),
                ...currentStepData,
              }}
              translateButtonComponent={translateButtonComponent}
              interaction={interaction}
              langValue={langValue}
              setIsLoading={setIsLoading}
              onUserInformationUpdate={async () =>
                !formPreview && await getContributions()}
            />
            {isSurvey
              && isMainUser(contributor)
              && interaction?.result_visible_on_front
              && hasSubmittedContribution && (
              <MainColumn column={journeyColumnTemplate}>
                <Link
                  data-cy="display-result"
                  className="vp-button"
                  href={`${pathname}/results`}
                  onClick={sendDataLayerDisplayResult}
                >
                  {t('SEE_RESULTS')}
                </Link>
              </MainColumn>
            )}
            {isSurvey && hasSubmittedContribution && isMainUser(contributor) && (
              <>
                <CompleteProfileBannerForm user={contributor} />
                <CrossSell projectId={interaction?.project?.id} />
              </>
            )}
          </>
        )}
        {shouldDisplaySubmitButton && (
          <MainColumn column={journeyColumnTemplate}>
            <VpButton
              data-cy="submitButton"
              data-testid="submit-form"
              disabled={checkIfNextStepDisabled}
              {...(isLastStep && {
                endSlot: <VpIcon name="chevron-right" />,
              })}
              loading={status === RequestStatus.PENDING}
              shape="squared"
              type="button"
              onClick={handleSubmit}
            >
              {submitButtonLabel}
            </VpButton>
          </MainColumn>
        )}
        {isCurrentStepQuestion && (
          <MainColumn column={journeyColumnTemplate}>
            {translateButtonComponent}
          </MainColumn>
        )}
      </Loader>
      {currentStep?.['@type'] !== CONCLUSION && (
        <FormNavigation
          prevDisabled={checkIfPreviousStepDisabled}
          nextDisabled={checkIfNextStepDisabled}
          prevAction={() => handleNextPrev(
            currentStepIndex - 1,
            handleSubmitQuestion,
            animateSlide,
          )}
          nextAction={() => handleNextPrev(
            currentStepIndex + 1,
            handleSubmitQuestion,
            animateSlide,
          )}
        />
      )}
    </>
  );
};
